import BaseClient from './baseClient';

type SearchProps = {
  searchTerms: string;
  language?: string;
  marketplace?: string;
  consumerChannelId?: string;
  searchOptions?: { [key: string]: string };
  sort?: string;
};

/**
 * Kingpin API Client
 * Calls Kingpin search/products/v1 API with explain=true
 */
class KingpinClient extends BaseClient {
  /**
   * Build query URL for Kingpin search products API
   */
  private buildQueryUrl(props: SearchProps): string {
    const queryParams: Record<string, any> = {
      language: props.language || 'zh-Hans',
      marketplace: props.marketplace || 'CN',
      channelId: props.consumerChannelId || '',
      queryType: 'products',
      searchTerms: props.searchTerms,
      explain: 'true',
    };

    // Filter out undefined/null values
    const filteredParams = Object.fromEntries(
      Object.entries(queryParams).filter(
        ([_, v]) => v !== undefined && v !== null && v !== '',
      ),
    );

    // Convert to query string
    const queryString = Object.entries(filteredParams)
      .map(
        ([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`,
      )
      .join('&');

    return `/proxy/search/products/v1?${queryString}`;
  }

  /**
   * Get explain data from Kingpin API
   * Returns explain.queryProcessorResponse directly
   */
  async getExplainData(props: SearchProps): Promise<any> {
    const url = this.buildQueryUrl(props);

    //console.log('Kingpin Explain API URL:', url);

    const data = await this.fetch(url, {
      method: 'GET',
    });

    //console.log('Kingpin API full response:', data);

    const result = {
      ...data?.explain?.queryProcessorResponse,
      elasticsearchQuery: data?.explain?.elasticSearchRequest,
      productRankingExplain: data?.explain?.productRankingResponse?.explain,
    };

    //console.log('Kingpin getExplainData result:', result);

    return result;
  }
}

/**
 * Export singleton instance
 */
const kingpinClient = new KingpinClient();

/**
 * React Query compatible function
 * Wraps the client method for use with useQuery
 */
export function explainSearch() {
  return async ({ queryKey }: { queryKey: any[] }) => {
    const [_key, props] = queryKey as [string, SearchProps];
    return kingpinClient.getExplainData(props);
  };
}

export default kingpinClient;
